import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  P3dModel,
  SwitchableType,
  getSingleSwitchable,
} from "./functions";
import QRCode from "qrcode";
import P3dEmbedAPI from "@p3d/embed-api";
import { deviceCanDisplayAR } from "../common/functions";
import ViewerModeIcons from "./mode-icons";

export interface SwitchableViewerProps {
  dataType: SwitchableType;
}

export const getARLink = (model: P3dModel): string => {
  return `https://p3d.in/ar/${model.id}`;
};

const SwitchableViewer = ({ dataType }: SwitchableViewerProps): JSX.Element => {
  const params = useParams();
  const [activeModel, setActiveModel] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [canInteract, setCanInteract] = useState(false);
  const [displayQRCode, setDisplayQRCode] = useState(false);
  const [p3dApi, setP3dApi] = useState<P3dEmbedAPI | null>(null);
  const experience = getSingleSwitchable(dataType, params.experienceID);
  const isXRCapable = false; // "xr" in window.navigator;
  useEffect((): void => {
    if (experience && canvasRef.current) {
      QRCode.toCanvas(
        canvasRef.current,
        `https://p3d.in/ar/${experience.models[activeModel].id}`,
        (error: any): void => {
          if (error) {
            console.error(error);
          }
        },
      );
    }
  }, [activeModel, canvasRef, experience]);
  useEffect((): void => {
    if (p3dApi && iframeRef) {
      p3dApi.setSpin(!canInteract);
      if (!canInteract) {
        p3dApi.resetCamera();
      }
    }
  }, [canInteract, p3dApi]);
  useEffect((): void => {
    if (iframeRef.current) {
      setP3dApi(new P3dEmbedAPI(iframeRef.current));
    }
  }, [setP3dApi]);
  if (!experience) {
    return <></>;
  }
  return (
    <>
      <div className="brand3d-viewer">
        <div className="brand3d-viewer-zone">
          <iframe
            ref={iframeRef}
            title={`Brand3D AR Experiences - ${experience.name}`}
            src={`https://p3d.in/e/${experience.models[activeModel].id}+load+spin+controls,border-hidden+bg-00000000+api`}
            seamless
          ></iframe>
          <div
            className={`brand3d-iteract-overlay ${canInteract ? "hidden" : ""}`}
            onClick={(): void => {
              setCanInteract(!canInteract);
            }}
          >
            <div className="brand3d-interact-notice">Click to interact</div>
          </div>
          <div className="brand3d-iframe-controls">
            <div className="embed-controls">
              <button
                type="button"
                className="brand3d-toggle-mode"
                onClick={(): void => setCanInteract(!canInteract)}
              >
                <ViewerModeIcons canInteract={canInteract} />
              </button>
            </div>
          </div>
          {experience.type === "single" ? (
            <div className="brand3d-ar-controls">
              {isXRCapable ? (
                <a
                  href={getARLink(experience.models[0])}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="brand3d-ar-button"
                >
                  View in your space
                </a>
              ) : (
                  <button
                    type="button"
                    className="brand3d-ar-button"
                    onClick={(): void => {
                      setDisplayQRCode(!displayQRCode);
                    }}
                  >
                    View in your space
                  </button>
              )}
            </div>
          ) : (
            ""
          )}
        </div>
        {experience.type === "switcher" ? (
          <div className="brand3d-viewer-sidebar">
            <div className="brand3d-experience-switcher">
              {experience.models.map(
                (
                  { name, thumbnail, id }: P3dModel,
                  index: number,
                ): JSX.Element => {
                  return (
                    <div
                      title={name}
                      className={`brand3d-experience-switch ${activeModel === index ? "active" : ""}`}
                      key={`experience-switch-${id}`}
                      onClick={() => {
                        setActiveModel(index);
                        setCanInteract(false);
                      }}
                    >
                      <div className="brand3d-switch-image">
                        <img src={thumbnail} alt="" />
                      </div>
                      <div className="brand3d-switch-name">
                        <span>{name}</span>
                      </div>
                    </div>
                  );
                },
              )}
            </div>
            <div className="brand3d-sidebar-button-container">
              {isXRCapable ? (
                <a
                  href={getARLink(experience.models[activeModel])}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="brand3d-ar-button"
                >
                  View in your space
                </a>
              ) : (
                <button
                  type="button"
                  className="brand3d-ar-button"
                  onClick={(): void => {
                    if (deviceCanDisplayAR()) {
                      // Create link and send device to AR
                    } else {
                      setDisplayQRCode(!displayQRCode);
                    }
                  }}
                >
                  View in your space
                </button>
              )}
            </div>
          </div>
        ) : null}
      </div>
      <dialog
        open={displayQRCode}
        className={`brand3d-modal-qr-code ${displayQRCode ? "visible" : "hidden"}`}
      >
        <div className="brand3d-qrcode-modal-body">
          <h2>Scan the QR code to open in AR</h2>
          <div className="brand3d-qrcode-container">
            <canvas ref={canvasRef} className="brand3d-ar-qr-code"></canvas>
          </div>
          <button
            className="brand3d-ar-button"
            onClick={(): void => setDisplayQRCode(false)}
          >
            Close
          </button>
        </div>
      </dialog>
    </>
  );
};

export default SwitchableViewer;
